import { createReducer, combineReducers, on } from '@ngrx/store';
import { Layer, LayerList } from '../models/layer.model';
import {
  deleteLayer,
  changeLayerOrder,
  setSelectedLayer,
  toggleLayerVisibility,
  toggleShowEffects,
  addNewLayer,
  updateLayer,
  setRender,
  addEffect,
  deleteEffect,
  selectEffect,
  updateRenderName,
  resetEditorState,
  loadPreset,
  moveLayer,
  moveEffect
} from './editor.actions';
import { generateUUID } from '../utils/store.utils';
import { EffectsType } from '../models/ui-effects.model';
import { migrateOldLayerToModern } from './editor.utils';
import { MoveLayerDirection } from '../models/properties.model';
import { merge } from 'lodash';

const intialLayerList: LayerList = {
}

export interface EditorState {
  renderId?: string;
  renderName: string
  layers: LayerList
  layerOrder: string[]
  selectedLayer?: string
  selectedEffect?: string
}

const initialState: EditorState = {
  layers: intialLayerList,
  layerOrder: [],
  renderName: 'New Render',
};

const selectedLayerReducer = createReducer(
  initialState.selectedLayer,
  on(resetEditorState, () => undefined),
  on(addNewLayer, (_state, {layer}) => layer.id),
  on(setSelectedLayer, (_state, {layerId}) => layerId),
  on(deleteLayer, (state, {layerId}) => {
    if (state === layerId) {
      return undefined
    }
  }),
)

const selectedEffectReducer = createReducer(
  initialState.selectedEffect,
  on(addNewLayer, () => undefined),
  on(deleteEffect, (state, {effectId}) => {
    if (state === effectId) {
      return undefined
    }
  }),
  on(selectEffect, (_state, {effectId}) => effectId),
)

const renderIdReducer = createReducer(
  initialState.renderId,
  on(setRender, (_state, {renderId}) => renderId),
  on(resetEditorState, () => undefined),
)

const renderNameReducer = createReducer(
  initialState.renderName,
  on(setRender, (_state, {renderName}) => renderName),
  on(updateRenderName, (_state, {name}) => name),
)

const layerOrderReducer = createReducer(
  initialState.layerOrder,
  on(loadPreset, (_state, {presetData}) => presetData.layerOrder),
  on(deleteLayer, (state, {layerId}) => {
    return state.filter(x => x !== layerId);
  }),
  on(setRender, (state, {layers, layerOrder}) => {
    return layerOrder
  }),
  on(addNewLayer, (state, {layer}) => {
    return [layer.id, ...state]
  }),
  on(changeLayerOrder, (state, {id, index}) => {
    var array = state.filter(x => x !== id);
    array.splice(index, 0, id);
    return array
  }),
  on(moveLayer, (state, {direction, layerId}) => {
    const curIdx = state.findIndex(x => x === layerId)
    if (curIdx === -1) {
      return state
    }
    if (curIdx === 0 && direction === 'up') {
      return state
    }
    if (curIdx === state.length - 1 && direction === 'down') {
      return state
    }
    const array = [...state]
    array.splice(curIdx, 1)
    if (direction === 'up') {
      array.splice(curIdx - 1, 0, layerId)
    } else {
      array.splice(curIdx + 1, 0, layerId)
    }
    return array

  }),
)

const layersReducer = createReducer(
  initialState.layers,
  on(loadPreset, (_state, {presetData}) => {
    const output: LayerList = {}
    for (const layer in presetData.layers) {
      const cleanLayer = JSON.parse(JSON.stringify(presetData.layers[layer])) as Layer
      const newLayer = migrateOldLayerToModern(cleanLayer)
      output[newLayer.id] = newLayer
    }
    return output
  }),
  on(setRender, (state, {layers}) => {
    const output: LayerList = {}
    for (const layer in layers) {
      const cleanLayer = JSON.parse(JSON.stringify(layers[layer])) as Layer
      const newLayer = migrateOldLayerToModern(cleanLayer)
      output[newLayer.id] = newLayer
    }
    return output
  }),
  on(deleteEffect, (state, {layerId, effectId}) => {
    return {
      ...state,
      [layerId]: {
        ...state[layerId],
        effects: state[layerId].effects.filter(x => x.id !== effectId)
      }
    }
  }),
  on(addEffect, (state, {layerId, effect}) => {
    return {
      ...state,
      [layerId]: {
        ...state[layerId],
        sharedSettings: {
          ...state[layerId].sharedSettings,
          showEffects: true
        },
        effects: [
          {
            id: generateUUID(),
            name: effect.label,
            effectType: effect.type as EffectsType,
          },
          ...state[layerId].effects
        ]
      }
    }
  }),
  on(toggleShowEffects, (state, {layerId}) => {
    return {
      ...state,
      [layerId]: {
        ...state[layerId],
        sharedSettings: {
          ...state[layerId].sharedSettings,
          showEffects: !state[layerId].sharedSettings.showEffects
        }
      } }
  }),
  on(toggleLayerVisibility, (state, {layerId}) => {
    return {
      ...state,
      [layerId]: {
        ...state[layerId],
        sharedSettings: {
          ...state[layerId].sharedSettings,
          visible: !state[layerId].sharedSettings.visible
        }
      }
    }
  }),
  on(updateLayer, (state, {layerId, layer}) => {
    return {
      ...state,
      [layerId]: layer
    }
  }),
  on(deleteLayer, (state, {layerId}) => {
    const newState = {...state}
    delete newState[layerId]
    return newState
  }),
  on(addNewLayer, (state, {layer}) => {
    return {
      ...state,
      [layer.id]: layer
    }
  }),
  on(moveEffect, (state, {direction, effectId, layerId}) => {
    let effects = state[layerId].effects.map(x => x)
    const effectIdx = effects.findIndex(x => x.id === effectId)
    if (direction === MoveLayerDirection.Up) {
      if (effectIdx === 0) {
        return state
      }
      const effect = effects[effectIdx]
      effects[effectIdx] = effects[effectIdx - 1]
      effects[effectIdx - 1] = effect
    }
    else {
      if (effectIdx === effects.length - 1) {
        return state
      }
      const effect = effects[effectIdx]
      effects[effectIdx] = effects[effectIdx + 1]
      effects[effectIdx + 1] = effect
    }
    return {
      ...state,
      [layerId]: {
        ...state[layerId],
        effects: effects
      }
    }
  })
)

export const editorReducer = combineReducers<EditorState>(
  {
    renderName: renderNameReducer,
    selectedLayer: selectedLayerReducer,
    renderId: renderIdReducer,
    layers: layersReducer,
    layerOrder: layerOrderReducer,
    selectedEffect: selectedEffectReducer
  },
)

